export const LINK_WORK_SUPPORTED_TYPES = [
  'ENTITY',
  'PROJECT',
  'TASK_GROUP',
  'TASK',
  'TICKET',
  'DEAL',
  'LEAD',
  'CONTACT',
  'EVENT',
  'CONTRACT',
  'INVOICE',
  'INVENTORY',
  'SUPPLIER',
  'DOCUMENT',
  'NOTE',
  'CUSTOM_COLLECTION',
  'ORGANIZATION',
] as const;

export type LinkWorkSupportedType = (typeof LINK_WORK_SUPPORTED_TYPES)[number];

/**
 * Types offered by the record References picker. A superset of the Link Work
 * types: knowledge articles are linkable as references but are deliberately
 * kept out of the time-entry Link Work types (time isn't tracked against
 * articles) — only callers that explicitly allow KNOWLEDGE_BASE get it.
 */
export const REFERENCE_PICKER_TYPES = [
  ...LINK_WORK_SUPPORTED_TYPES,
  'KNOWLEDGE_BASE',
] as const;

export type ReferencePickerType = (typeof REFERENCE_PICKER_TYPES)[number];

const LINK_WORK_SUPPORTED_TYPE_SET = new Set<string>(LINK_WORK_SUPPORTED_TYPES);
const REFERENCE_PICKER_TYPE_SET = new Set<string>(REFERENCE_PICKER_TYPES);

function normalizeAliases(value: string): string {
  const normalized = value.trim().toUpperCase().replace(/[\s-]+/g, '_');

  if (normalized === 'SUBTASK' || normalized === 'SUBTASKS') return 'TASK';
  if (normalized === 'TASK_GROUP' || normalized === 'TASK_GROUPS') return 'TASK_GROUP';
  if (normalized === 'TASKGROUP' || normalized === 'TASKGROUPS') return 'TASK_GROUP';
  if (normalized === 'CLIENT' || normalized === 'CLIENTS') return 'ENTITY';
  if (normalized === 'ORG' || normalized === 'ORGS' || normalized === 'ORGANIZATIONS') return 'ORGANIZATION';
  if (normalized === 'CUSTOM_COLLECTIONS' || normalized === 'COLLECTION') return 'CUSTOM_COLLECTION';
  if (normalized === 'COLLECTIONS') return 'CUSTOM_COLLECTION';
  if (normalized === 'VENDOR' || normalized === 'VENDORS') return 'SUPPLIER';
  if (normalized === 'INVENTORY_ITEM' || normalized === 'INVENTORY_ITEMS') return 'INVENTORY';

  return normalized;
}

export function normalizeLinkWorkType(value: string): LinkWorkSupportedType | null {
  const normalized = normalizeAliases(value);

  if (LINK_WORK_SUPPORTED_TYPE_SET.has(normalized)) {
    return normalized as LinkWorkSupportedType;
  }

  return null;
}

export function normalizeLinkWorkTypes(values?: string[]): LinkWorkSupportedType[] {
  const normalizedValues = (values ?? [])
    .map(normalizeLinkWorkType)
    .filter((value): value is LinkWorkSupportedType => value !== null);

  return Array.from(new Set(normalizedValues));
}

export function getLinkWorkAllowedModuleTypes(values?: string[]): LinkWorkSupportedType[] {
  const normalizedValues = normalizeLinkWorkTypes(values);
  return normalizedValues.length > 0 ? normalizedValues : [...LINK_WORK_SUPPORTED_TYPES];
}

/** Like normalizeLinkWorkTypes, but over the wider reference-picker set. */
export function normalizeReferencePickerTypes(values?: string[]): ReferencePickerType[] {
  const normalizedValues = (values ?? [])
    .map(value => {
      const normalized = normalizeAliases(value);
      return REFERENCE_PICKER_TYPE_SET.has(normalized)
        ? (normalized as ReferencePickerType)
        : null;
    })
    .filter((value): value is ReferencePickerType => value !== null);

  return Array.from(new Set(normalizedValues));
}
